/**
 * Workspace currency is `pricing.currency` on GET /v1/organization/settings
 * (TRY, EUR or USD; the engine default is TRY). The login panel has no
 * session, so it reads the last value this module stored. Before that, the
 * engine default is used — it is not a second currency setting.
 */
import { useEffect, useState } from "react";
import { isE2eMockMode } from "./e2eFixture";
import { fetchOrgSettings, type PricingCurrency } from "./orgSettingsApi";

const KEY = "semanticad.workspaceCurrency";

/** Engine `PRICING_DEFAULTS.currency`. */
export const WORKSPACE_CURRENCY_DEFAULT: PricingCurrency = "TRY";

export function isPricingCurrency(value: string | null | undefined): value is PricingCurrency {
  return value === "TRY" || value === "EUR" || value === "USD";
}

export function readWorkspaceCurrency(): PricingCurrency {
  try {
    const raw = localStorage.getItem(KEY);
    if (isPricingCurrency(raw)) return raw;
  } catch {
    /* private mode / tests */
  }
  return WORKSPACE_CURRENCY_DEFAULT;
}

export function rememberWorkspaceCurrency(currency: string | null | undefined): void {
  if (!isPricingCurrency(currency)) return;
  try {
    localStorage.setItem(KEY, currency);
  } catch {
    /* ignore quota */
  }
}

/** Org pricing currency, or the engine default until settings load. */
export function useWorkspaceCurrency(): PricingCurrency {
  const [currency, setCurrency] = useState<PricingCurrency>(readWorkspaceCurrency);
  useEffect(() => {
    if (isE2eMockMode()) return;
    let cancel = false;
    fetchOrgSettings()
      .then((settings) => {
        if (cancel) return;
        const next = settings.pricing?.currency;
        if (!isPricingCurrency(next)) return;
        rememberWorkspaceCurrency(next);
        setCurrency(next);
      })
      .catch(() => {
        /* keep the remembered code or the engine default */
      });
    return () => {
      cancel = true;
    };
  }, []);
  return currency;
}
